import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IcSearch,
  IcClose,
  IcPlus,
  IcUsers,
  IcAward,
  IcGift,
  IcBanknote,
  IcFilters,
  IcShoppingBag,
  IcStar,
  IcChevronRight,
} from '../../theme/icons';
import Swal from '../../theme/swal';
import customerService from '../../services/customerService';
import clientCategoryService from '../../services/clientCategoryService';
import authService from '../../services/authService';
import CustomerDetailModal from '../../pages/customers/CustomerDetailModal';
import PhoneLink from '../../components/PhoneLink';
import KI from '../../theme/palette';

const PAGE_SIZE = 30;

function CustomerCard({ customer, onOpen }) {
  const displayName =
    customer.full_name ||
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
    'Client sans nom';

  const credit = Number(customer.credit) || 0;
  const isGold = (customer.category_name || '').toLowerCase().includes('gold');

  // Studio 2026-08-14 : panneau client identique à la carte de la page Clients
  // web (CustomerList) — nom + badge, tuiles Achats/Score/Points, bandeau
  // « Reste à payer », rangée d'actions (Modifier/Supprimer mobile + Voir).
  return (
    <div
      onClick={() => onOpen(customer.id)}
      className="bg-white rounded-2xl border border-gray-500 p-5 flex flex-col hover:border-gray-600 transition-all cursor-pointer"
    >
      {/* Top: name + category */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="min-w-0">
          <p className="font-semibold text-gray-800 leading-tight truncate">{displayName}</p>
          <p className="text-xs text-gray-400 truncate">
            {customer.phone ? <PhoneLink phone={customer.phone} /> : (customer.email || '—')}
          </p>
        </div>
        {/* Fidélité — Étiquettes §9 : FIDÈLE bordé neutre, FIDÈLE GOLD dégradé amber. */}
        {customer.category_name && (
          <span
            className={`bdg shrink-0 ${isGold ? 'bdg-gold' : 'bdg-fidele'} px-2.5 py-1 text-xs rounded-full`}
          >
            <IcAward className="text-[10px]" /> {customer.category_name}
          </span>
        )}
      </div>

      {/* Achats + Score + Points */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
          <IcShoppingBag className="text-gray-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] text-gray-400 uppercase tracking-wide truncate">Achats</p>
            <p className="text-sm font-bold text-gray-800 tabular-nums truncate">{customer.total_sales || 0}</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
          <IcStar className="text-amber-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] text-gray-400 uppercase tracking-wide truncate">Score</p>
            <p className="text-sm font-bold text-gray-800 tabular-nums truncate">{customer.score || 0}</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
          <IcGift className="text-green-600 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] text-gray-400 uppercase tracking-wide truncate">Points</p>
            <p className="text-sm font-bold text-gray-800 tabular-nums truncate">{(Number(customer.points) || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Reste à payer — fond gris, bordure rouge clair si crédit, verte sinon */}
      <div className={`flex items-end justify-between mb-1 rounded-xl px-3 py-2 bg-gray-100 border ${credit > 0 ? 'border-red-100' : 'border-green-100'}`}>
        <span className="text-xs text-gray-500">Reste à payer</span>
        {credit > 0 ? (
          <span className="text-lg font-bold text-red-700 tabular-nums">{credit.toFixed(3)} DT</span>
        ) : (
          <span className="text-lg font-bold text-green-700 tabular-nums">0.000 DT</span>
        )}
      </div>
      {credit > 0 && customer.credit_maximum > 0 && (
        <p className="text-xs text-gray-400 text-right">Max : {Number(customer.credit_maximum).toFixed(3)} DT</p>
      )}

      {/* « Voir » n'est qu'un repère visuel : TOUTE la carte ouvre la fiche
          (Ahmed 2026-08-16) — le clic remonte au onClick de la carte. */}
      <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100">
        <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-gray-700">
          Voir <IcChevronRight className="text-xs" />
        </span>
      </div>
    </div>
  );
}

export default function MobileCustomerList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sortMode, setSortMode] = useState('alpha');
  const [totals, setTotals] = useState(null);
  const canManage = authService.isManagerOrAdmin();

  const load = useCallback(async (p, s, append) => {
    setLoading(true);
    try {
      const { results, count: total, totals: serverTotals } = await customerService.getCustomersPage({
        page: p,
        pageSize: PAGE_SIZE,
        search: s,
        ordering: sortMode,
      });
      setCount(total);
      setTotals(serverTotals);
      setCustomers((prev) => (append ? [...prev, ...results] : results));
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  }, [sortMode]);

  // Cliquer une carte stat active déjà triée revient au tri alphabétique.
  const toggleSort = (mode) => setSortMode((cur) => (cur === mode ? 'alpha' : mode));

  // Debounced search — resets to page 1 whenever the term changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load(1, search, false);
    }, 350);
    return () => clearTimeout(t);
  }, [search, load]);

  // Category lookup (one-shot) — same service the desktop list uses.
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await clientCategoryService.getAllCategories();
        const arr = data.results ? data.results : Array.isArray(data) ? data : [];
        setCategories(arr);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next, search, true);
  };

  // Studio 2026-08-14 : la fiche client s'ouvre dans le même panneau que la
  // page Clients web (CustomerDetailModal), plus de navigation vers /clients/:id.
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const handleOpen = (id) => setSelectedCustomerId(id);
  const handleEdit = (id) => navigate(`/clients/${id}/edit`);

  const handleDelete = (customer) => {
    const name =
      customer.full_name ||
      `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
      'ce client';
    Swal.fire({
      title: 'Supprimer ce client ?',
      text: name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.peacock,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await customerService.deleteCustomer(customer.id);
        setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
        setCount((c) => Math.max(0, c - 1));
        setSelectedCustomerId(null);
      } catch (err) {
        console.error('Error deleting customer:', err);
        Swal.fire('Erreur', 'Échec de la suppression du client.', 'error');
      }
    });
  };

  // Category filter is applied client-side over the loaded rows (the server
  // list endpoint doesn't take a category param); search + pagination stay
  // server-side like the desktop list.
  const visible = useMemo(() => {
    if (!category) return customers;
    return customers.filter((c) => String(c.category_name || '') === category);
  }, [customers, category]);

  // Totaux sur TOUT l'ensemble filtré (totals serveur, comme le web) — repli
  // sur une somme locale si l'API ne les renvoie pas.
  const creditCount = totals
    ? Number(totals.credit_count) || 0
    : customers.filter((c) => (Number(c.credit) || 0) > 0).length;
  const creditTotal = totals
    ? Number(totals.credit_total) || 0
    : customers.reduce((s, c) => s + (Number(c.credit) || 0), 0);
  const pointsTotal = totals
    ? Number(totals.points_total) || 0
    : customers.reduce((s, c) => s + (Number(c.points) || 0), 0);

  return (
    <div className="w-full px-4 py-6">
      {/* Studio 2026-08-14 : nom de page = même chip que « Produits » (/products).
          Ahmed 2026-08-16 : bouton + en haut à droite, comme MobileProductList
          (l'ancien bouton flottant bleu en bas est retiré). */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcUsers size={13} /> Clients
        </span>
        {canManage && (
          <button
            onClick={() => navigate('/clients/add')}
            aria-label="Ajouter un client"
            className="btn-p h-9 w-9 rounded-xl shrink-0"
          >
            <IcPlus className="text-sm" />
          </button>
        )}
      </div>
      {/* Search + Filtres — même rangée que la page Clients web. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1 min-w-0">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou n° carte..."
            className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <IcClose />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className={`px-4 py-2.5 rounded-xl border flex items-center justify-center font-medium transition-colors shrink-0 ${showFilters ? 'bg-white text-gray-900 border-gray-900 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
        >
          <IcFilters className="mr-2 text-sm" /> Filtres
        </button>
      </div>

      {/* Panneau de filtres — carte repliable comme sur le web ; les cartes
          stats (Studio 2026-08-14) vivent dedans, pas dans la page. */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-2xl mb-4 p-4 space-y-3">
          {categories.length > 0 && (
            <select
              aria-label="Catégorie client"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            >
              <option value="">Catégorie client</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}
          {customers.length > 0 && (
            /* Cartes KPI (Composants §5) : blanche = info · teintée = cliquable-tri
               · bordée vert + drapeau = tri actif. */
            <div className="grid grid-cols-1 gap-3">
          <div className="kpi rounded-2xl p-4 text-left">
            <div className="flex items-center justify-between mb-1">
              <span className="kpi-label uppercase text-sm tracking-normal">Total clients</span>
              <span className="h-9 w-9 rounded-xl flex items-center justify-center bg-gray-100 text-gray-500"><IcUsers /></span>
            </div>
            <p className="kpi-value text-2xl text-gray-900">{count}</p>
            <p className="kpi-sub text-xs mt-1 flex items-center gap-1">
              <IcUsers className="text-[10px]" /> Clients enregistrés
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleSort('points')}
            className={`kpi kpi-green rounded-2xl p-4 text-left transition-all ${sortMode === 'points' ? 'kpi-active' : ''}`}
          >
            {sortMode === 'points' && <span className="kpi-flag">Tri actif</span>}
            <div className="flex items-center justify-between mb-1">
              <span className="kpi-label uppercase text-sm tracking-normal">Points cumulés</span>
              <span className="h-9 w-9 rounded-xl flex items-center justify-center bg-white text-green-600"><IcGift /></span>
            </div>
            <p className="kpi-value text-2xl text-green-700">{pointsTotal.toFixed(2)}</p>
            <p className="kpi-sub text-xs mt-1">
              {sortMode === 'points' ? 'Trié : plus de points' : 'Trier par plus de points'}
            </p>
          </button>
          <button
            type="button"
            onClick={() => toggleSort('credit')}
            className={`kpi kpi-coral rounded-2xl p-4 text-left transition-all ${sortMode === 'credit' ? 'kpi-active' : ''}`}
          >
            {sortMode === 'credit' && <span className="kpi-flag">Tri actif</span>}
            <div className="flex items-center justify-between mb-1">
              <span className="kpi-label uppercase text-sm tracking-normal">Crédit total dû</span>
              <span className="h-9 w-9 rounded-xl flex items-center justify-center bg-white text-red-600"><IcBanknote /></span>
            </div>
            <p className="kpi-value text-2xl text-red-700">{creditTotal.toFixed(3)} DT</p>
            <p className="kpi-sub text-xs mt-1">
              {sortMode === 'credit' ? 'Trié : plus grand reste à payer' : `${creditCount} avec crédit · trier par reste à payer`}
            </p>
          </button>
            </div>
          )}
        </div>
      )}

      {loading && customers.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcUsers className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucun client trouvé</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((c) => (
            <CustomerCard key={c.id} customer={c} onOpen={handleOpen} />
          ))}
        </div>
      )}

      {!loading && !category && customers.length > 0 && customers.length < count && (
        <button
          onClick={loadMore}
          className="btn-s mt-4 w-full py-2.5 rounded-xl font-medium transition-colors"
        >
          Charger plus ({customers.length}/{count})
        </button>
      )}

      {selectedCustomerId != null && (
        <CustomerDetailModal
          customerId={selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
          onEdit={handleEdit}
          onDelete={(id) => {
            const c = customers.find((x) => x.id === id);
            handleDelete(c || { id });
          }}
        />
      )}
    </div>
  );
}
